import type { NativeLoginContext } from '../utils/nativeLogin'
import type {
  Account,
  AccountSecurity,
  CloudSession,
  DashboardData,
  Device,
  ManagedWebSession,
  ManagedWebSessionStatus,
  PersonalWorkItem,
  PersonalWorkSession,
  PersonalWorkStatus,
  Runtime,
  RuntimeSession,
  RuntimeCloudDisplayNameUpdate,
  HelpDocument,
  HelpDocumentSummary,
} from '../types'
import { isNormalizedRuntimeAlias, runtimeDisplayName } from '../utils/runtimeAlias'
import { assertCurrentIdentity, captureIdentity, registerIdentityCleanup } from './request-context'

const baseUrl = '/bff/user'
let csrfToken = ''
const deviceVersions = new Map<string, number>()
const sessionVersions = new Map<string, number>()

export function resetSessionState(preserveCsrf = false): void {
  if (!preserveCsrf) csrfToken = ''
  deviceVersions.clear()
  sessionVersions.clear()
}
registerIdentityCleanup(options => resetSessionState(options.preserveCsrf))

export interface DeviceAuthorizationRequest {
  nonce: string
  devicePublicKey: string
  deviceLabel: string
  sessionProfile: 'TEMPORARY' | 'TRUSTED'
  proof: string
}

export interface OidcCallback {
  code: string
  state: string
}

export type LoginProviderId = 'github' | 'wechat' | 'qq'

export interface LoginCapabilities {
  contractRevision: 'hive-login-capabilities-v1'
  clientId: string
  defaultMethod: 'phone_sms'
  providers: Array<{ id: LoginProviderId; authorizationPath: string }>
}

export interface AccountLifecycleMutation {
    accountId: string
    status: Account['status']
    securityVersion: number
    effectiveAt?: string
    deletionDueAt?: string
    replacementDeviceId?: string
}

export interface RuntimeClaimPreview {
  challengeId: string
  runtimeRecordId: string
  runtimeVersion: string
  expectedVersion: number
  expiresAt: string
}

export interface RuntimeClaimSmsChallenge {
  challengeId: string
  expiresInSeconds: number
  resendAfterSeconds: number
  phoneNumberMasked: string
}

export interface RuntimeClaimApproval {
  challengeId: string
  runtimeRecordId: string
  status: 'APPROVED'
  resourceVersion: number
}

export interface RuntimeUnlinkResult {
  runtimeRecordId: string
  status: 'UNLINKED'
  authorityGeneration: number
  fencingEpoch: number
  resourceVersion: number
  unlinkedAt: string
}

export interface SmsLoginStart {
  state: string
  challengeId: string
  expiresInSeconds: number
  resendAfterSeconds: number
}

export interface AccountSecurityChallenge {
  challengeId: string
  bindingId: string
  expiresInSeconds: number
  resendAfterSeconds: number
}

export interface WebLaunchGrant {
  intentId: string
  ticketId: string
  webHttpsOrigin: string
  webClientPath: string
  expiresAt: string
}

export class UserApiError extends Error {
  constructor(public readonly status: number, public readonly code?: string, message = 'Request failed', public readonly retryAfterSeconds?: number) {
    super(message)
    this.name = 'UserApiError'
  }
}

export const isUserApiError = (value: unknown, code?: string): value is UserApiError =>
  value instanceof UserApiError && (code === undefined || value.code === code)

async function requestWithResponse<T>(path: string, options?: RequestInit, reportExpiredSession = true): Promise<{ value: T; headers: Headers; status: number }> {
  const context = options?.credentials === 'omit' ? undefined : captureIdentity()
  const method = options?.method?.toUpperCase() ?? 'GET'
  const headers = {
    Accept: 'application/json',
    ...(options?.body ? { 'Content-Type': 'application/json' } : {}),
    ...(options?.credentials !== 'omit' && method !== 'GET' && method !== 'HEAD' && csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
    ...options?.headers,
  }
  const response = await fetch(`${baseUrl}${path}`, { ...options, credentials: options?.credentials ?? 'include', headers })
  const responseBody = response.status === 204 ? '' : await response.text()
  // A cancelled request from the previous account must not expire a replacement session.
  options?.signal?.throwIfAborted()
  if (context) assertCurrentIdentity(context)
  if (!response.ok) {
    if (response.status === 401 && reportExpiredSession && typeof window !== 'undefined') {
      window.dispatchEvent(new Event('hivecloud:user-auth-expired'))
    }
    let problem: { code?: string; detail?: string; message?: string } = {}
    try { problem = responseBody ? JSON.parse(responseBody) as typeof problem : {} } catch { /* non-JSON failure */ }
    const retryAfter = response.headers.get('Retry-After')
    const retryAfterSeconds = retryAfter && /^\d+$/.test(retryAfter) ? Number(retryAfter) : undefined
    throw new UserApiError(response.status, problem.code, problem.detail ?? problem.message ?? response.statusText,
      retryAfterSeconds !== undefined && Number.isSafeInteger(retryAfterSeconds) && retryAfterSeconds > 0 && retryAfterSeconds <= 86400 ? retryAfterSeconds : undefined)
  }
  return { value: responseBody ? JSON.parse(responseBody) as T : undefined as T, headers: response.headers, status: response.status }
}

async function request<T>(path: string, options?: RequestInit, reportExpiredSession = true): Promise<T> {
  return (await requestWithResponse<T>(path, options, reportExpiredSession)).value
}

function items(value: unknown): Record<string, any>[] {
  if (Array.isArray(value)) return value as Record<string, any>[]
  if (value && typeof value === 'object' && Array.isArray((value as { items?: unknown }).items)) return (value as { items: Record<string, any>[] }).items
  throw new UserApiError(502, 'invalid_collection_response')
}

function optionalText(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function requiredSecurityText(value: unknown, field: string): string {
  const text = optionalText(value)
  if (!text || text.length > 255) throw new UserApiError(502, 'invalid_account_security_response', `Invalid ${field}`)
  return text
}

function requiredSecurityUuid(value: unknown): string {
  const text = requiredSecurityText(value, 'accountId')
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(text)) {
    throw new UserApiError(502, 'invalid_account_security_response', 'Invalid accountId')
  }
  return text
}

function optionalPositiveInteger(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0 ? value : undefined
}

function optionalNonNegativeInteger(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : undefined
}

function optionalStringList(value: unknown): string[] | undefined {
  return Array.isArray(value) && value.every((item) => typeof item === 'string')
    ? [...value]
    : undefined
}

function normalizeLoginCapabilities(value: unknown): LoginCapabilities {
  const fallback: LoginCapabilities = {
    contractRevision: 'hive-login-capabilities-v1',
    clientId: 'hive-cloud-user-web',
    defaultMethod: 'phone_sms',
    providers: [],
  }
  if (!value || typeof value !== 'object') return fallback
  const document = value as Record<string, unknown>
  if (Object.keys(document).length !== 4
    || document.contractRevision !== fallback.contractRevision
    || typeof document.clientId !== 'string'
    || document.clientId.length === 0
    || document.clientId.length > 255
    || document.clientId.trim() !== document.clientId
    || document.defaultMethod !== fallback.defaultMethod
    || !Array.isArray(document.providers)
    || document.providers.length > 3) return fallback
  const known = new Set<LoginProviderId>(['github', 'wechat', 'qq'])
  const seen = new Set<LoginProviderId>()
  const providers: LoginCapabilities['providers'] = []
  for (const provider of document.providers) {
    if (!provider || typeof provider !== 'object') return fallback
    const entry = provider as Record<string, unknown>
    const id = entry.id as LoginProviderId
    if (Object.keys(entry).length !== 2 || !known.has(id) || seen.has(id)
      || entry.authorizationPath !== `/hive/v1/auth/provider-authorizations/${id}`) return fallback
    seen.add(id)
    providers.push({ id, authorizationPath: entry.authorizationPath })
  }
  return { ...fallback, clientId: document.clientId, providers }
}

function identityLocaleHeaders(): Record<string, string> {
  if (typeof document === 'undefined') return { 'X-Hive-Locale': 'zh-CN' }
  return { 'X-Hive-Locale': document.documentElement.lang === 'en-US' ? 'en-US' : 'zh-CN' }
}

function normalizeRuntimeCloudDisplayNameUpdate(
  value: Record<string, any>,
  expectedRuntimeRecordId: string,
): RuntimeCloudDisplayNameUpdate {
  const cloudDisplayName = value.cloudDisplayName
  const version = optionalPositiveInteger(value.cloudDisplayNameVersion)
  const ownershipEpoch = optionalPositiveInteger(value.ownershipEpoch)
  const updatedAt = optionalText(value.updatedAt)
  if (value.runtimeRecordId !== expectedRuntimeRecordId
    || (cloudDisplayName !== null && !isNormalizedRuntimeAlias(cloudDisplayName))
    || version === undefined
    || ownershipEpoch === undefined
    || !updatedAt
    || !Number.isFinite(Date.parse(updatedAt))) {
    throw new UserApiError(502, 'runtime_cloud_display_name_response_invalid')
  }
  return {
    runtimeRecordId: expectedRuntimeRecordId,
    ownershipEpoch,
    cloudDisplayName,
    cloudDisplayNameVersion: version,
    updatedAt,
  }
}

function normalizeRuntime(value: Record<string, any>): Runtime {
  const projection = value.projection ?? {}
  const hasProjection = value.projection !== null
    && value.projection !== undefined
    && typeof value.projection === 'object'
  const id = String(value.runtimeRecordId)
  const ownershipEpoch = optionalPositiveInteger(value.ownershipEpoch)
  if (ownershipEpoch === undefined) throw new UserApiError(502, 'runtime_directory_response_invalid')
  const cloudDisplayNameVersion = optionalPositiveInteger(value.cloudDisplayNameVersion)
  if (value.cloudDisplayName !== null
    && !isNormalizedRuntimeAlias(value.cloudDisplayName)) {
    throw new UserApiError(502, 'runtime_cloud_display_name_response_invalid')
  }
  const cloudDisplayNameOverride = isNormalizedRuntimeAlias(value.cloudDisplayName)
    ? value.cloudDisplayName
    : undefined
  if (cloudDisplayNameVersion === undefined) {
    throw new UserApiError(502, 'runtime_cloud_display_name_response_invalid')
  }
  const presence = String(value.presence ?? 'UNKNOWN').toLowerCase()
  const readiness = hasProjection
    ? String(projection.readiness ?? 'UNKNOWN').toLowerCase()
    : 'unknown'
  const normalizedReadiness: Runtime['readiness'] = ({
    starting: 'starting',
    ready: 'ready',
    degraded: 'degraded',
    recovering: 'recovering',
    needs_setup: 'needs_setup',
    stopped: 'blocked',
    unknown: 'unknown',
  } as const)[readiness as 'starting' | 'ready' | 'degraded' | 'recovering' | 'needs_setup' | 'stopped' | 'unknown'] ?? 'unknown'
  const region = optionalText(value.region)
  const resourceVersion = optionalPositiveInteger(value.resourceVersion)
  const runtimeProtocolVersion = optionalPositiveInteger(
    hasProjection ? projection.runtimeProtocolVersion : value.runtimeProtocolVersion,
  )
  const capabilities = optionalStringList(
    hasProjection ? projection.capabilities : value.capabilities,
  )
  const webHttpsOrigin = optionalText(projection.webHttpsOrigin)
  const webClientPath = optionalText(projection.webClientPath)
  const websocketPath = optionalText(projection.websocketPath)
  const webEndpointExpiresAt = optionalText(projection.webEndpointExpiresAt)
  const startedAt = optionalText(projection.startedAt)
  const freeDiskBytes = optionalNonNegativeInteger(projection.freeDiskBytes)
  const connectionCapabilities = optionalStringList(projection.connectionCapabilities)
  const deviceName = optionalText(value.deviceName)
  const osName = optionalText(value.osName)
  const osVersion = optionalText(value.osVersion)
  const osArch = optionalText(value.osArch)
  const cpuModel = optionalText(value.cpuModel)
  const cpuLogicalCores = optionalPositiveInteger(value.cpuLogicalCores)
  const totalMemoryBytes = optionalPositiveInteger(value.totalMemoryBytes)
  const lastSeenIp = optionalText(value.lastSeenIp)
  const claimedAt = optionalText(value.claimedAt)
  return {
    id,
    ownershipEpoch,
    cloudDisplayNameVersion,
    name: id,
    cloudDisplayName: runtimeDisplayName(id, cloudDisplayNameOverride, deviceName),
    version: String((hasProjection ? projection.runtimeVersion : undefined) ?? value.runtimeVersion ?? 'unknown'),
    status: presence === 'online' ? 'online' : presence === 'offline' ? 'offline' : 'pending',
    presence: presence === 'online'
      ? 'present'
      : presence === 'degraded'
        ? 'degraded'
        : presence === 'offline'
          ? 'away'
          : 'unknown',
    readiness: normalizedReadiness,
    lastSeen: String(value.lastHeartbeatAt ?? projection.observedAt ?? ''),
    ...(resourceVersion ? { resourceVersion } : {}),
    ...(runtimeProtocolVersion ? { runtimeProtocolVersion } : {}),
    ...(capabilities ? { capabilities } : {}),
    ...(webHttpsOrigin ? { webHttpsOrigin } : {}),
    ...(webClientPath ? { webClientPath } : {}),
    ...(websocketPath ? { websocketPath } : {}),
    ...(webEndpointExpiresAt ? { webEndpointExpiresAt } : {}),
    ...(startedAt ? { startedAt } : {}),
    ...(freeDiskBytes !== undefined ? { freeDiskBytes } : {}),
    ...(connectionCapabilities ? { connectionCapabilities } : {}),
    ...(region ? { region } : {}),
    ...(deviceName ? { deviceName } : {}),
    ...(osName ? { osName } : {}),
    ...(osVersion ? { osVersion } : {}),
    ...(osArch ? { osArch } : {}),
    ...(cpuModel ? { cpuModel } : {}),
    ...(cpuLogicalCores ? { cpuLogicalCores } : {}),
    ...(totalMemoryBytes ? { totalMemoryBytes } : {}),
    ...(lastSeenIp ? { lastSeenIp } : {}),
    ...(cloudDisplayNameOverride ? { cloudDisplayNameOverride } : {}),
    ...(claimedAt ? { claimedAt } : {}),
  }
}

function normalizeDevice(value: Record<string, any>): Device {
  return {
    id: String(value.cloudAccountDeviceId),
    name: String(value.deviceLabel ?? value.cloudAccountDeviceId),
    platform: 'Hive Cloud',
    browser: 'WebCrypto',
    location: String(value.lastLoginLocation ?? '—'),
    lastSeen: String(value.lastActivityAt ?? value.lastAuthenticatedAt ?? value.createdAt ?? ''),
    current: Boolean(value.current),
    status: String(value.status ?? 'ACTIVE') as Device['status'],
    trusted: String(value.trustStatus) === 'TRUSTED',
    trustStatus: String(value.trustStatus ?? 'UNTRUSTED') as Device['trustStatus'],
    ...(typeof value.createdAt === 'string' ? { createdAt: value.createdAt } : {}),
    ...(typeof value.trustedAt === 'string' ? { trustedAt: value.trustedAt } : {}),
    ...(typeof value.securityVersion === 'number' ? { securityVersion: value.securityVersion } : {}),
  } as Device
}

function normalizeSession(value: Record<string, any>): CloudSession {
  return {
    id: String(value.cloudSessionId),
    deviceName: String(value.deviceLabel ?? value.cloudAccountDeviceId),
    location: '—',
    createdAt: String(value.issuedAt ?? ''),
    lastSeen: String(value.lastRotatedAt ?? value.issuedAt ?? ''),
    current: Boolean(value.currentSession),
    ...(typeof value.securityVersion === 'number' ? { securityVersion: value.securityVersion } : {}),
  } as CloudSession
}

const MANAGED_WEB_SESSION_STATUSES = new Set<ManagedWebSessionStatus>([
  'ACTIVE',
  'REVOKE_PENDING',
  'REVOKED',
  'EXPIRED',
  'UNVERIFIABLE',
])

function normalizeRuntimeSession(value: Record<string, any>): RuntimeSession {
  const statuses = ['PENDING_ACTIVATION', 'ACTIVE', 'REVOKE_PENDING', 'REVOKED', 'CLOSED', 'EXPIRED', 'UNVERIFIABLE']
  const resourceVersion = optionalPositiveInteger(value.resourceVersion)
  if (!statuses.includes(value.status) || !resourceVersion || value.clientKind !== 'WEB'
    || !optionalText(value.managedSessionId) || !optionalText(value.runtimeRecordId)
    || !Number.isFinite(Date.parse(value.createdAt)) || !Number.isFinite(Date.parse(value.expiresAt))) {
    throw new UserApiError(502, 'runtime_session_response_invalid')
  }
  return {
    id: value.managedSessionId, runtimeRecordId: value.runtimeRecordId,
    clientLabel: optionalText(value.clientLabel) ?? '', status: value.status,
    resourceVersion, createdAt: value.createdAt, expiresAt: value.expiresAt,
  }
}

function normalizeManagedWebSession(value: Record<string, any>): ManagedWebSession {
  const status = String(value.status) as ManagedWebSessionStatus
  const resourceVersion = optionalPositiveInteger(value.resourceVersion)
  const controlVersion = optionalPositiveInteger(value.controlVersion)
  if (!MANAGED_WEB_SESSION_STATUSES.has(status) || !resourceVersion || !controlVersion) {
    throw new UserApiError(502, 'web_session_response_invalid')
  }
  return {
    id: String(value.managedWebSessionId),
    runtimeRecordId: String(value.runtimeRecordId),
    runtimeInstanceId: String(value.runtimeInstanceId),
    runtimeSessionId: String(value.runtimeSessionId),
    status,
    resourceVersion,
    controlVersion,
    createdAt: String(value.createdAt),
    expiresAt: String(value.expiresAt),
    ...(typeof value.revokeRequestedAt === 'string'
      ? { revokeRequestedAt: value.revokeRequestedAt }
      : {}),
    ...(typeof value.revokeAcknowledgedAt === 'string'
      ? { revokeAcknowledgedAt: value.revokeAcknowledgedAt }
      : {}),
  }
}

function normalizeWebLaunchGrant(value: Record<string, any>): WebLaunchGrant {
  const fields = ['intentId', 'ticketId', 'webHttpsOrigin', 'webClientPath', 'expiresAt'] as const
  if (fields.some((field) => !optionalText(value[field]))) {
    throw new UserApiError(502, 'web_launch_grant_response_invalid')
  }
  return Object.fromEntries(fields.map((field) => [field, String(value[field])])) as unknown as WebLaunchGrant
}

function normalizeRuntimeUnlink(value: Record<string, any>, expectedRuntimeRecordId: string): RuntimeUnlinkResult {
  const runtimeRecordId = optionalText(value.runtimeRecordId)
  const authorityGeneration = optionalPositiveInteger(value.authorityGeneration)
  const fencingEpoch = optionalPositiveInteger(value.fencingEpoch)
  const resourceVersion = optionalPositiveInteger(value.resourceVersion)
  const unlinkedAt = optionalText(value.unlinkedAt)
  if (runtimeRecordId !== expectedRuntimeRecordId || value.status !== 'UNLINKED'
    || !authorityGeneration || !fencingEpoch || !resourceVersion || !unlinkedAt
    || !Number.isFinite(Date.parse(unlinkedAt))) {
    throw new UserApiError(502, 'runtime_unlink_response_invalid')
  }
  return { runtimeRecordId, status: 'UNLINKED', authorityGeneration, fencingEpoch, resourceVersion, unlinkedAt }
}

const PERSONAL_WORK_STATUSES = new Set<PersonalWorkStatus>(['TODO', 'IN_PROGRESS', 'IN_REVIEW', 'COMPLETED', 'ARCHIVED'])

function normalizePersonalWork(value: Record<string, any>): PersonalWorkItem {
  const id = optionalText(value.personalWorkItemId)
  const runtimeRecordId = optionalText(value.runtimeRecordId)
  const title = optionalText(value.title)
  const status = String(value.status ?? '') as PersonalWorkStatus
  const resourceVersion = optionalPositiveInteger(value.resourceVersion)
  if (!id || !runtimeRecordId || !title || !PERSONAL_WORK_STATUSES.has(status) || !resourceVersion
    || typeof value.createdAt !== 'string' || typeof value.updatedAt !== 'string') {
    throw new UserApiError(502, 'personal_work_response_invalid')
  }
  return {
    id, runtimeRecordId, title, status, resourceVersion,
    createdAt: value.createdAt,
    updatedAt: value.updatedAt,
    ...(typeof value.archivedAt === 'string' ? { archivedAt: value.archivedAt } : {}),
  }
}

function normalizePersonalWorkSession(value: Record<string, any>): PersonalWorkSession {
  return normalizeManagedWebSession(value)
}

function newIdempotencyKey(): string {
  return crypto.randomUUID().replaceAll('-', '')
}

function normalizeAccountStatus(value: unknown): Account['status'] {
  const status = String(value ?? '').toLowerCase()
  return status === 'deletion_pending' ? 'pending_deletion' : status as Account['status']
}

function normalizeAccount(value: Record<string, any>): Account {
  if (!value || !optionalText(value.accountId) || !optionalText(value.displayName)
    || !['active', 'pending_deletion', 'recovery'].includes(normalizeAccountStatus(value.status))
    || optionalNonNegativeInteger(value.securityVersion) === undefined) {
    throw new UserApiError(502, 'invalid_account_response')
  }
  return {
    id: String(value.accountId),
    displayName: String(value.displayName),
    status: normalizeAccountStatus(value.status),
    securityVersion: value.securityVersion,
    ...(typeof value.email === 'string' ? { email: value.email } : {}),
    ...(typeof value.mfaEnabled === 'boolean' ? { mfaEnabled: value.mfaEnabled } : {}),
    ...(typeof value.createdAt === 'string' ? { memberSince: value.createdAt } : {}),
    ...(typeof value.deletionRequestedAt === 'string' ? { deletionRequestedAt: value.deletionRequestedAt } : {}),
    ...(typeof value.deletionDueAt === 'string' ? { deletionDueAt: value.deletionDueAt } : {}),
  }
}

function normalizeAccountLifecycleMutation(value: Record<string, any>): AccountLifecycleMutation {
  if (!value || !optionalText(value.accountId)
    || !['active', 'pending_deletion', 'recovery'].includes(normalizeAccountStatus(value.status))
    || optionalNonNegativeInteger(value.securityVersion) === undefined) throw new UserApiError(502, 'invalid_account_lifecycle_response')
  return {
    accountId: String(value.accountId),
    status: normalizeAccountStatus(value.status),
    securityVersion: Number(value.securityVersion),
    ...(typeof value.effectiveAt === 'string' ? { effectiveAt: value.effectiveAt } : {}),
    ...(typeof value.deletionDueAt === 'string' ? { deletionDueAt: value.deletionDueAt } : {}),
    ...(typeof value.replacementDeviceId === 'string' ? { replacementDeviceId: value.replacementDeviceId } : {}),
  }
}

export interface CursorPage<T> {
  items: T[]
  nextCursor?: string
  total?: number
}

export const userApi = {
  async helpDocuments(params: { locale?: string; category?: string; limit?: number } = {}, signal?: AbortSignal): Promise<CursorPage<HelpDocumentSummary>> {
    const query = new URLSearchParams()
    if (params.locale) query.set('locale', params.locale)
    if (params.category) query.set('category', params.category)
    query.set('limit', String(params.limit ?? 50))
    const response = await requestWithResponse<unknown>(`/help-documents?${query}`, { signal })
    return { items: items(response.value).map(value => ({ slug: String(value.slug), title: String(value.title ?? value.name ?? value.slug), ...(typeof value.category === 'string' ? { category: value.category } : {}), ...(typeof value.sortOrder === 'number' ? { sortOrder: value.sortOrder } : {}), ...(typeof value.updatedAt === 'string' ? { updatedAt: value.updatedAt } : {}) })) }
  },
  async helpDocument(slug: string, locale?: string, signal?: AbortSignal): Promise<HelpDocument> {
    const query = locale ? `?locale=${encodeURIComponent(locale)}` : ''
    const value = await request<Record<string, any>>(`/help-documents/${encodeURIComponent(slug)}${query}`, { signal })
    return { slug: String(value.slug ?? slug), title: String(value.title ?? value.name ?? slug), markdown: String(value.markdown ?? value.content ?? ''), ...(typeof value.category === 'string' ? { category: value.category } : {}), ...(typeof value.sortOrder === 'number' ? { sortOrder: value.sortOrder } : {}), ...(typeof value.revision === 'number' ? { revision: value.revision } : {}), ...(typeof value.publishedAt === 'string' ? { publishedAt: value.publishedAt } : {}) }
  },
  async session(signal?: AbortSignal): Promise<{ authenticated: boolean; csrfToken?: string }> {
    const context = captureIdentity()
    const session = await request<{ authenticated: boolean; csrfToken?: string }>('/auth/session', { signal }, false)
    assertCurrentIdentity(context)
    if (!session || typeof session.authenticated !== 'boolean'
      || (session.csrfToken !== undefined && (typeof session.csrfToken !== 'string' || !session.csrfToken.trim()
        || session.csrfToken.includes('\r') || session.csrfToken.includes('\n')))) {
      throw new UserApiError(502, 'invalid_session_response')
    }
    csrfToken = session.csrfToken ?? ''
    return session
  },
  async deviceChallenge(): Promise<{ nonce: string; clientId: string }> {
    return await request('/auth/device-challenge', { method: 'POST' })
  },
  async login(): Promise<{ redirect: string }> {
    return await request('/auth/login', { method: 'POST', headers: identityLocaleHeaders() })
  },
  async loginCapabilities(): Promise<LoginCapabilities> {
    return normalizeLoginCapabilities(await request<unknown>('/auth/login-capabilities', undefined, false))
  },
  async providerLogin(providerId: LoginProviderId): Promise<{ redirect: string }> {
    return await request(`/auth/login/${encodeURIComponent(providerId)}`, {
      method: 'POST', headers: identityLocaleHeaders(),
    }, false)
  },
  async register(authorization: DeviceAuthorizationRequest): Promise<{ redirect: string }> {
    return await request('/auth/register', { method: 'POST', headers: identityLocaleHeaders(), body: JSON.stringify(authorization) })
  },
  async stepUp(authorization: DeviceAuthorizationRequest): Promise<{ redirect: string }> {
    return await request('/auth/step-up', { method: 'POST', headers: identityLocaleHeaders(), body: JSON.stringify(authorization) })
  },
  async enrollMfa(authorization: DeviceAuthorizationRequest): Promise<{ redirect: string }> {
    return await request('/auth/mfa-enrollment', { method: 'POST', headers: identityLocaleHeaders(), body: JSON.stringify(authorization) })
  },
  async manageMfaRecoveryCodes(authorization: DeviceAuthorizationRequest): Promise<{ redirect: string }> {
    return await request('/auth/mfa-recovery-codes', { method: 'POST', headers: identityLocaleHeaders(), body: JSON.stringify(authorization) })
  },
  async callbackChallenge(state: string): Promise<{ nonce: string; clientId: string }> {
    return await request('/auth/callback/device-challenge', {
      method: 'POST', body: JSON.stringify({ state }),
    })
  },
  async callback(callback: OidcCallback, authorization?: DeviceAuthorizationRequest): Promise<{ authenticated: true; csrfToken?: string }> {
    if (authorization) {
      await request('/auth/callback', {
        method: 'POST', body: JSON.stringify({ code: callback.code, state: callback.state, authorization }),
      })
    } else {
      // Registration, MFA and step-up authorize the device before the redirect.
      // The BFF validates their bound, one-time state through its GET callback.
      const query = new URLSearchParams({ code: callback.code, state: callback.state })
      await request(`/auth/callback?${query}`, { method: 'GET', cache: 'no-store' }, false)
    }
    const session = await this.session()
    if (!session.authenticated) throw new UserApiError(401, 'session_not_established')
    return { ...session, authenticated: true }
  },
  async dashboard(): Promise<DashboardData> {
    const [account, runtimePage, devicePage, sessionPage] = await Promise.all([
      this.account(), this.runtimesPage(), this.devicesPage(), this.sessionsPage(),
    ])
    return {
      account,
      runtimes: runtimePage.items,
      devices: devicePage.items,
      sessions: sessionPage.items,
      counts: {
        runtimes: { value: runtimePage.items.length, exact: !runtimePage.nextCursor },
        devices: {
          value: devicePage.total ?? devicePage.items.length,
          exact: typeof devicePage.total === 'number' || !devicePage.nextCursor,
        },
        sessions: { value: sessionPage.items.length, exact: !sessionPage.nextCursor },
      },
    }
  },
  async runtimesPage(cursor?: string, signal?: AbortSignal): Promise<CursorPage<Runtime>> {
    const query = new URLSearchParams({ limit: '50' })
    if (cursor) query.set('cursor', cursor)
    const response = await requestWithResponse<unknown>(`/runtimes?${query}`, { signal })
    return {
      items: items(response.value).map(normalizeRuntime),
      nextCursor: response.headers.get('X-Hive-Next-Cursor') ?? undefined,
    }
  },
  async runtimes(): Promise<Runtime[]> {
    return (await this.runtimesPage()).items
  },
  async runtime(id: string, signal?: AbortSignal): Promise<Runtime> {
    const value = await request<Record<string, any>>(`/runtimes/${encodeURIComponent(id)}`, { signal })
    const runtime = normalizeRuntime(value)
    if (runtime.id.toLowerCase() !== id.toLowerCase()) {
      throw new UserApiError(502, 'runtime_detail_response_invalid')
    }
    return runtime
  },
  async updateRuntimeCloudDisplayName(
    runtimeRecordId: string,
    cloudDisplayName: string | null,
    expectedCloudDisplayNameVersion: number,
  ): Promise<RuntimeCloudDisplayNameUpdate> {
    if ((cloudDisplayName !== null && !isNormalizedRuntimeAlias(cloudDisplayName))
      || optionalPositiveInteger(expectedCloudDisplayNameVersion) === undefined) {
      throw new UserApiError(400, 'runtime_cloud_display_name_request_invalid')
    }
    const value = await request<Record<string, any>>(`/runtimes/${encodeURIComponent(runtimeRecordId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ cloudDisplayName, expectedCloudDisplayNameVersion }),
    })
    return normalizeRuntimeCloudDisplayNameUpdate(value, runtimeRecordId)
  },
  async nativeLoginConfiguration(): Promise<unknown> {
    return await request('/auth/native/login-configuration', { method: 'POST', body: '{}', credentials: 'omit', redirect: 'error' }, false)
  },
  async nativeSmsStart(context: NativeLoginContext, phoneNumber: string, locale: string, termsAccepted: boolean): Promise<Omit<SmsLoginStart, 'state'>> {
    return await request('/auth/native/sms-challenges', { method: 'POST', credentials: 'omit', redirect: 'error', body: JSON.stringify({ nonce: context.nonce, clientId: context.clientId, phoneNumber, locale, termsAccepted }) }, false)
  },
  async nativeSmsVerify(context: NativeLoginContext, challengeId: string, smsCode: string, termsAccepted: boolean): Promise<void> {
    await request(`/auth/native/sms-challenges/${encodeURIComponent(challengeId)}/verify`, { method: 'POST', credentials: 'omit', redirect: 'error', body: JSON.stringify({ nonce: context.nonce, clientId: context.clientId, smsCode, termsAccepted }) }, false)
  },
  async nativeSmsAuthorize(context: NativeLoginContext): Promise<unknown> {
    return await request('/auth/native/sms-authorizations', { method: 'POST', credentials: 'omit', redirect: 'error', body: JSON.stringify(context) }, false)
  },
  async smsStart(authorization: DeviceAuthorizationRequest, phoneNumber: string, termsAccepted: boolean): Promise<SmsLoginStart> {
    return await request('/auth/sms/start', {
      method: 'POST',
      headers: identityLocaleHeaders(),
      body: JSON.stringify({ authorization, phoneNumber, termsAccepted }),
    }, false)
  },
  async smsVerify(state: string, challengeId: string, smsCode: string, termsAccepted: boolean): Promise<void> {
    await request('/auth/sms/verify', {
      method: 'POST',
      body: JSON.stringify({ state, challengeId, smsCode, termsAccepted }),
    }, false)
  },
  async smsComplete(state: string): Promise<{ authenticated: true; csrfToken?: string }> {
    await request('/auth/sms/complete', { method: 'POST', body: JSON.stringify({ state }) })
    const session = await this.session()
    if (!session.authenticated) throw new UserApiError(401, 'session_not_established')
    return { ...session, authenticated: true }
  },
  async createWebLaunchGrant(
    runtimeRecordId: string,
    expectedResourceVersion: number,
    launchSecretSha256: string,
  ): Promise<WebLaunchGrant> {
    const value = await request<Record<string, any>>(
      `/runtimes/${encodeURIComponent(runtimeRecordId)}/web-launch-grants`,
      {
        method: 'POST',
        headers: { 'Idempotency-Key': crypto.randomUUID().replaceAll('-', '') },
        body: JSON.stringify({
          protocolVersion: 'web-launch/v1',
          expectedResourceVersion,
          launchSecretSha256,
        }),
      },
    )
    return normalizeWebLaunchGrant(value)
  },
  async previewRuntimeClaim(userCode: string): Promise<RuntimeClaimPreview> {
    return await request('/runtime-claim-challenges/preview', { method: 'POST', body: JSON.stringify({ userCode }) })
  },
  async startRuntimeClaimSms(userCode: string, expectedVersion: number): Promise<RuntimeClaimSmsChallenge> {
    return await request('/runtime-claim-challenges/sms-challenges', { method: 'POST', body: JSON.stringify({ userCode, expectedVersion }) })
  },
  async approveRuntimeClaim(userCode: string, expectedVersion: number, smsChallengeId: string, smsCode: string): Promise<RuntimeClaimApproval> {
    return await request('/runtime-claim-challenges/approve', { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID().replaceAll('-', '') }, body: JSON.stringify({ userCode, expectedVersion, smsChallengeId, smsCode }) })
  },

  async startRuntimeUnlinkSms(runtimeRecordId: string, expectedVersion: number): Promise<RuntimeClaimSmsChallenge> {
    return await request(`/runtimes/${encodeURIComponent(runtimeRecordId)}/unlink/sms-challenges`, { method: 'POST', body: JSON.stringify({ expectedVersion }) })
  },
  async unlinkRuntime(runtimeRecordId: string, expectedVersion: number, proof: { smsChallengeId: string; smsCode: string }): Promise<RuntimeUnlinkResult> {
    const value = await request<Record<string, any>>(`/runtimes/${encodeURIComponent(runtimeRecordId)}/unlink`, {
      method: 'POST',
      body: JSON.stringify({ expectedVersion, ...proof }),
    })
    return normalizeRuntimeUnlink(value, runtimeRecordId)
  },
  async devicesPage(cursor?: string, signal?: AbortSignal): Promise<CursorPage<Device>> {
    const query = new URLSearchParams({ limit: '5' })
    if (cursor) query.set('cursor', cursor)
    const context = captureIdentity()
    const value = await request<{ items?: unknown; nextCursor?: unknown; total?: unknown }>(`/devices?${query}`, { signal })
    assertCurrentIdentity(context)
    const result = items(value).map(normalizeDevice)
    result.forEach((item: Device & { securityVersion?: number }) => {
      if (optionalNonNegativeInteger(item.securityVersion) !== undefined) deviceVersions.set(item.id, item.securityVersion!)
      else deviceVersions.delete(item.id)
    })
    const total = typeof value.total === 'number' && Number.isSafeInteger(value.total) && value.total >= result.length
      ? value.total
      : undefined
    return { items: result, nextCursor: typeof value.nextCursor === 'string' ? value.nextCursor : undefined, total }
  },
  async devices(): Promise<Device[]> { return (await this.devicesPage()).items },
  async renameDevice(id: string, deviceLabel: string): Promise<Device> {
    const value = await request<Record<string, any>>(`/devices/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ deviceLabel }),
    })
    return normalizeDevice(value)
  },
  async revokeDevice(id: string): Promise<void> {
    if (!deviceVersions.has(id)) throw new UserApiError(409, 'device_security_version_unavailable')
    await request(`/devices/${encodeURIComponent(id)}/revoke`, { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID().replaceAll('-', '') }, body: JSON.stringify({ expectedSecurityVersion: deviceVersions.get(id) }) })
  },
  async sessionsPage(cursor?: string, signal?: AbortSignal): Promise<CursorPage<CloudSession>> {
    const query = new URLSearchParams({ limit: '50' })
    if (cursor) query.set('cursor', cursor)
    const context = captureIdentity()
    const value = await request<{ items?: unknown; nextCursor?: unknown }>(`/cloud-sessions?${query}`, { signal })
    assertCurrentIdentity(context)
    const result = items(value).map(normalizeSession)
    result.forEach((item: CloudSession & { securityVersion?: number }) => {
      if (optionalNonNegativeInteger(item.securityVersion) !== undefined) sessionVersions.set(item.id, item.securityVersion!)
      else sessionVersions.delete(item.id)
    })
    return { items: result, nextCursor: typeof value.nextCursor === 'string' ? value.nextCursor : undefined }
  },
  async sessions(): Promise<CloudSession[]> { return (await this.sessionsPage()).items },
  async revokeSession(id: string): Promise<void> {
    if (!sessionVersions.has(id)) throw new UserApiError(409, 'session_security_version_unavailable')
    await request(`/cloud-sessions/${encodeURIComponent(id)}/revoke`, { method: 'POST', headers: { 'Idempotency-Key': crypto.randomUUID().replaceAll('-', '') }, body: JSON.stringify({ expectedSecurityVersion: sessionVersions.get(id), reason: 'owner_sign_out' }) })
  },
  async runtimeWebSessionsPage(cursor?: string, runtimeRecordId?: string, signal?: AbortSignal): Promise<CursorPage<RuntimeSession>> {
    const query = new URLSearchParams({ limit: '25', clientKind: 'WEB' })
    if (cursor) query.set('cursor', cursor)
    if (runtimeRecordId) query.set('runtimeRecordId', runtimeRecordId)
    const value = await request<{ items?: unknown; nextCursor?: unknown }>(`/runtime-sessions?${query}`, { signal })
    return {
      items: items(value).map(normalizeRuntimeSession),
      nextCursor: typeof value.nextCursor === 'string' ? value.nextCursor : undefined,
    }
  },
  async revokeRuntimeSession(session: RuntimeSession): Promise<void> {
    const protocolVersion = 'account-runtime-session-revoke/v2'
    const operationId = crypto.randomUUID()
    const receipt = await request<unknown>(`/runtime-sessions/${encodeURIComponent(session.id)}/revoke`, {
      method: 'POST',
      body: JSON.stringify({ protocolVersion, operationId, expectedResourceVersion: session.resourceVersion }),
    })
    const fields = ['protocolVersion', 'operationId', 'managedSessionId', 'status', 'resourceVersion', 'controlVersion']
    const value = receipt as Record<string, unknown> | null
    if (!value || typeof value !== 'object' || Array.isArray(value)
      || Object.keys(value).length !== fields.length || !fields.every(field => Object.hasOwn(value, field))
      || value.protocolVersion !== protocolVersion || value.operationId !== operationId || value.managedSessionId !== session.id
      || typeof value.managedSessionId !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/.test(value.managedSessionId)
      || !['REVOKE_PENDING', 'CLOSED', 'REVOKED', 'EXPIRED', 'UNVERIFIABLE'].includes(value.status as string)
      || optionalPositiveInteger(value.resourceVersion) === undefined || (value.resourceVersion as number) < session.resourceVersion
      || optionalNonNegativeInteger(value.controlVersion) === undefined) {
      throw new UserApiError(502, 'runtime_session_revoke_response_invalid')
    }
  },
  async personalWorkPage(cursor?: string, signal?: AbortSignal): Promise<CursorPage<PersonalWorkItem>> {
    const query = new URLSearchParams({ limit: '50' })
    if (cursor) query.set('cursor', cursor)
    const response = await requestWithResponse<{ items?: unknown; nextCursor?: unknown }>(`/personal-work-items?${query}`, { signal })
    return {
      items: items(response.value).map(normalizePersonalWork),
      nextCursor: response.headers.get('X-Hive-Next-Cursor') ?? (typeof response.value.nextCursor === 'string' ? response.value.nextCursor : undefined),
    }
  },
  async personalWork(id: string, signal?: AbortSignal): Promise<PersonalWorkItem> {
    const result = normalizePersonalWork(await request<Record<string, any>>(`/personal-work-items/${encodeURIComponent(id)}`, { signal }))
    if (result.id.toLowerCase() !== id.toLowerCase()) throw new UserApiError(502, 'personal_work_response_invalid')
    return result
  },
  async createPersonalWork(runtimeRecordId: string, expectedRuntimeResourceVersion: number, title: string): Promise<PersonalWorkItem> {
    const result = normalizePersonalWork(await request<Record<string, any>>('/personal-work-items', {
      method: 'POST',
      headers: { 'Idempotency-Key': newIdempotencyKey() },
      body: JSON.stringify({ protocolVersion: 'personal-work-item/v1', runtimeRecordId, expectedRuntimeResourceVersion, title }),
    }))
    if (result.runtimeRecordId.toLowerCase() !== runtimeRecordId.toLowerCase()) throw new UserApiError(502, 'personal_work_response_invalid')
    return result
  },
  async updatePersonalWork(id: string, expectedResourceVersion: number, patch: { title?: string; status?: PersonalWorkStatus }): Promise<PersonalWorkItem> {
    const result = normalizePersonalWork(await request<Record<string, any>>(`/personal-work-items/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      headers: { 'Idempotency-Key': newIdempotencyKey() },
      body: JSON.stringify({ protocolVersion: 'personal-work-item/v1', expectedResourceVersion, ...patch }),
    }))
    if (result.id.toLowerCase() !== id.toLowerCase() || result.resourceVersion <= expectedResourceVersion) throw new UserApiError(502, 'personal_work_response_invalid')
    return result
  },
  async openPersonalWork(id: string, expectedResourceVersion: number, expectedRuntimeResourceVersion: number, launchSecretSha256: string): Promise<WebLaunchGrant> {
    return normalizeWebLaunchGrant(await request<Record<string, any>>(`/personal-work-items/${encodeURIComponent(id)}/web-launch-grants`, {
      method: 'POST',
      headers: { 'Idempotency-Key': newIdempotencyKey() },
      body: JSON.stringify({ protocolVersion: 'personal-work-launch/v1', expectedResourceVersion, expectedRuntimeResourceVersion, launchSecretSha256 }),
    }))
  },
  async personalWorkSessionsPage(id: string, cursor?: string, signal?: AbortSignal): Promise<CursorPage<PersonalWorkSession>> {
    const query = new URLSearchParams({ limit: '50' })
    if (cursor) query.set('cursor', cursor)
    const value = await request<{ items?: unknown; nextCursor?: unknown }>(`/personal-work-items/${encodeURIComponent(id)}/sessions?${query}`, { signal })
    return {
      items: items(value).map(normalizePersonalWorkSession),
      nextCursor: typeof value.nextCursor === 'string' ? value.nextCursor : undefined,
    }
  },
  async revokeWebSession(session: ManagedWebSession): Promise<ManagedWebSession> {
    const value = await request<Record<string, any>>(`/web-sessions/${encodeURIComponent(session.id)}/revoke`, {
      method: 'POST',
      headers: { 'Idempotency-Key': crypto.randomUUID().replaceAll('-', '') },
      body: JSON.stringify({
        protocolVersion: 'web-session-revoke/v1',
        expectedControlVersion: session.controlVersion,
        reasonCode: 'USER_REQUESTED',
      }),
    })
    const result = normalizeManagedWebSession(value)
    if (result.id !== session.id || result.runtimeRecordId !== session.runtimeRecordId
      || result.runtimeInstanceId !== session.runtimeInstanceId || result.runtimeSessionId !== session.runtimeSessionId
      || result.resourceVersion < session.resourceVersion || result.controlVersion < session.controlVersion
      || !['REVOKE_PENDING', 'REVOKED', 'CLOSED', 'EXPIRED', 'UNVERIFIABLE'].includes(result.status)) {
      throw new UserApiError(502, 'web_session_revoke_response_invalid')
    }
    return result
  },
  async revokeAllSessions(): Promise<void> {
    const identity = captureIdentity()
    const account = await this.account()
    assertCurrentIdentity(identity)
    if (identity.accountId && account.id !== identity.accountId) throw new UserApiError(502, 'account_identity_mismatch')
    await request('/cloud-sessions/revoke-all', {
      method: 'POST',
      headers: { 'Idempotency-Key': crypto.randomUUID().replaceAll('-', '') },
      body: JSON.stringify({ expectedSecurityVersion: account.securityVersion, reason: 'owner_sign_out' }),
    })
  },
  async account(signal?: AbortSignal): Promise<Account> {
    const identity = captureIdentity()
    const value = await request<Record<string, any>>('/account', { signal })
    assertCurrentIdentity(identity)
    const account = normalizeAccount(value)
    if (identity.accountId && account.id !== identity.accountId) throw new UserApiError(502, 'account_identity_mismatch')
    return account
  },
  async accountSecurity(signal?: AbortSignal): Promise<AccountSecurity> {
    const value = await request<Record<string, any>>('/account/security', { signal })
    if (typeof value.phoneBound !== 'boolean'
      || (value.phoneNumber !== undefined && value.phoneNumber !== null
        && (typeof value.phoneNumber !== 'string' || !value.phoneNumber.trim()))) {
      throw new UserApiError(502, 'invalid_account_security_response', 'Invalid account security response')
    }
    return {
      accountId: requiredSecurityUuid(value.accountId),
      userName: requiredSecurityText(value.userName, 'userName'),
      displayName: requiredSecurityText(value.displayName, 'displayName'),
      ...(typeof value.phoneNumber === 'string' ? { phoneNumber: value.phoneNumber } : {}),
      phoneBound: value.phoneBound === true,
    }
  },
  async setPassword(newPassword: string): Promise<void> {
    const response = await requestWithResponse('/account/security/password', { method: 'POST', body: JSON.stringify({ newPassword }) })
    if (response.status !== 204) throw new UserApiError(502, 'account_security_outcome_unconfirmed')
  },
  async startPhoneBinding(phoneNumber: string): Promise<AccountSecurityChallenge> {
    return await request('/account/security/phone/challenge', {
      method: 'POST', body: JSON.stringify({ phoneNumber }),
    })
  },
  async verifyPhoneBinding(challengeId: string, bindingId: string, smsCode: string): Promise<void> {
    await request('/account/security/phone/verify', {
      method: 'POST', body: JSON.stringify({ challengeId, bindingId, smsCode }),
    })
  },
  async startPasswordReset(phoneNumber: string): Promise<AccountSecurityChallenge> {
    const value = await request<AccountSecurityChallenge>('/auth/password-reset/start', {
      method: 'POST', body: JSON.stringify({ phoneNumber }),
    }, false)
    if (!value || typeof value.challengeId !== 'string' || !value.challengeId.trim() || value.challengeId.length > 64
      || typeof value.bindingId !== 'string' || !value.bindingId.trim() || value.bindingId.length > 128
      || !Number.isSafeInteger(value.expiresInSeconds) || value.expiresInSeconds <= 0
      || !Number.isSafeInteger(value.resendAfterSeconds) || value.resendAfterSeconds < 0
      || !Number.isSafeInteger(Date.now() + value.expiresInSeconds * 1000)
      || !Number.isSafeInteger(Date.now() + value.resendAfterSeconds * 1000)) {
      throw new UserApiError(502, 'invalid_account_security_challenge')
    }
    return value
  },
  async verifyPasswordReset(
    challengeId: string, bindingId: string, smsCode: string, newPassword: string
  ): Promise<void> {
    const response = await requestWithResponse('/auth/password-reset/verify', {
      method: 'POST', body: JSON.stringify({ challengeId, bindingId, smsCode, newPassword }),
    }, false)
    if (response.status !== 204) throw new UserApiError(502, 'account_security_outcome_unconfirmed')
  },
  async deleteAccount(): Promise<AccountLifecycleMutation> {
    return normalizeAccountLifecycleMutation(await request<Record<string, any>>('/account/deletion', {
      method: 'POST', body: JSON.stringify({ acceptRetentionNotice: true }),
    }))
  },
  async cancelDeletion(): Promise<AccountLifecycleMutation> {
    return normalizeAccountLifecycleMutation(await request<Record<string, any>>('/account/deletion/cancel', {
      method: 'POST', body: JSON.stringify({}),
    }))
  },
  async recoverDevice(lostDeviceId: string, replacementDeviceAuthorizationNonce: string): Promise<AccountLifecycleMutation> {
    return normalizeAccountLifecycleMutation(await request<Record<string, any>>('/account/recovery', {
      method: 'POST',
      body: JSON.stringify({ lostDeviceId, replacementDeviceAuthorizationNonce }),
    }))
  },
  async logout(): Promise<void> {
    try { await request('/auth/logout', { method: 'POST' }, false) } finally { csrfToken = '' }
  },
}
